import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AnimatePresence, motion } from "framer-motion";
import PropTypes from "prop-types";
import { useCallback } from "react";

import { Button } from "@transfr-inc/dashboard-components/forms";

import { SlideTransitions } from "@lib/animation.helpers";
import { buttonTracking } from "@lib/tracking.helpers";

import "./budget-slideshow.footer.scss";

export const BudgetSlideshowFooter = ({
  slideshow,
  nextButtonEnabled,
  onStartOver,
  onViewPathway,
  isViewSummaryEnabled,
  onViewSummary,
}) => {
  const isFirstSlide = slideshow.currentIndex === 0;
  const isLastSlide = slideshow.currentIndex === slideshow.totalSlides - 1;

  const getNextButtonContent = () => {
    const lockIcon = (
      <motion.div {...SlideTransitions}>
        <FontAwesomeIcon icon={["fa-regular", "lock"]} />
      </motion.div>
    );

    const arrowIcon = (
      <motion.div {...SlideTransitions}>
        <FontAwesomeIcon icon={["fa-regular", "arrow-right"]} />
      </motion.div>
    );

    let nextButtonContent = (
      <>
        Next
        {nextButtonEnabled && arrowIcon}
      </>
    );

    if (isFirstSlide) {
      nextButtonContent = (
        <>
          Set Up Budget
          {nextButtonEnabled && arrowIcon}
        </>
      );
    }

    if (isLastSlide) {
      nextButtonContent = "View Full Pathway";
    }

    return (
      <>
        {!nextButtonEnabled && lockIcon}
        {nextButtonContent}
      </>
    );
  };

  const nextButtonHandler = useCallback(() => {
    if (isLastSlide) {
      onViewPathway();
    } else {
      slideshow.next();
    }
  }, [isLastSlide, onViewPathway, slideshow]);

  return (
    <div className="actions-container">
      {!isFirstSlide && !isLastSlide && (
        <>
          <Button
            onClick={slideshow.previous}
            className="back-button"
            {...buttonTracking("budgeting-game", "back")}
          >
            <FontAwesomeIcon icon={["fa-regular", "arrow-left"]} />
            Back
          </Button>

          <div className="slide-indicator">
            {isViewSummaryEnabled && (
              <Button
                onClick={onViewSummary}
                className="view-summary-button"
                {...buttonTracking("budgeting-game", "view-summary")}
              >
                View Summary
              </Button>
            )}
            {!isLastSlide && (
              <>
                {slideshow.currentIndex + 1} of {slideshow.totalSlides - 1}
              </>
            )}
          </div>
        </>
      )}

      <AnimatePresence>
        <Button
          onClick={nextButtonHandler}
          disabled={!nextButtonEnabled}
          primary
          className="next-button"
          {...buttonTracking("budgeting-game", "next")}
        >
          {getNextButtonContent()}
        </Button>
      </AnimatePresence>
      {isLastSlide && (
        <Button
          onClick={onStartOver}
          className="start-over-button"
          {...buttonTracking("budgeting-game", "start-over")}
        >
          Start Over
        </Button>
      )}
    </div>
  );
};

BudgetSlideshowFooter.propTypes = {
  nextButtonEnabled: PropTypes.bool.isRequired,
  onStartOver: PropTypes.func.isRequired,
  onViewPathway: PropTypes.func,
  isViewSummaryEnabled: PropTypes.bool,
  onViewSummary: PropTypes.func,
  slideshow: PropTypes.shape({
    currentIndex: PropTypes.number.isRequired,
    next: PropTypes.func.isRequired,
    previous: PropTypes.func.isRequired,
    totalSlides: PropTypes.number.isRequired,
  }).isRequired,
};
